import React from 'react';
import PropTypes from 'prop-types';

// Security: Input sanitization function
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

const SummaryCard = ({ configuration = {}, price = '', isLoading = false }) => {
  const hasConfiguration = configuration && typeof configuration === 'object' && Object.keys(configuration).length > 0;

  // Render configuration entries
  const renderConfigurationEntries = () => {
    if (!hasConfiguration) return null;

    return Object.entries(configuration).map(([key, value]) => {
      if (!key || (value === null || value === undefined)) {
        return null;
      }

const safeKey = key;
      const safeValue = String(value);
      return (
        <div key={safeKey} className="flex flex-col sm:flex-row sm:justify-between sm:items-center w-full space-y-1 sm:space-y-0">
          <span className="text-gray-400 text-sm sm:text-sm md:text-xs flex-shrink-0">{safeKey}</span>
          <span className="text-white font-medium text-sm sm:text-sm md:text-xs flex-shrink-0 sm:text-right">{safeValue}</span>
        </div>
      );
    }).filter(Boolean);
  };

  // Render price section
  const renderPriceSection = () => {
    if (!price) {
      return (
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center w-full space-y-1 sm:space-y-0">
          <span className="text-white font-semibold text-base sm:text-base md:text-sm flex-shrink-0">Total Price</span>
          <span className="text-gray-400 text-sm sm:text-sm md:text-xs flex-shrink-0 sm:text-right">Not available</span>
        </div>
      );
    }

    const safePrice = sanitizeText(price);
    return (
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center w-full space-y-1 sm:space-y-0">
        <span className="text-white font-semibold text-base sm:text-base md:text-sm flex-shrink-0">Total Price</span>
        <span className="text-[#e0eacc] font-semibold text-base sm:text-base md:text-sm flex-shrink-0 sm:text-right">{safePrice}</span>
      </div>
    );
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="bg-[#121212] p-5 sm:p-4 md:p-4 rounded-md mb-4 sm:mb-2 md:mb-2 w-full max-w-full border border-[#333333] shadow-md animate-pulse mt-0 sm:mt-12 md:mt-[6.3rem]">
        <h3 className="font-semibold text-white text-xl sm:text-lg md:text-lg mb-4 sm:mb-3 md:mb-3">Summary</h3>
        <div className="border-t border-[#333333] pt-4 sm:pt-3 md:pt-3 space-y-4 sm:space-y-3 md:space-y-3 w-full">
          {[1, 2, 3, 4, 5].map((_, i) => (
            <div key={i} className="flex flex-col sm:flex-row sm:justify-between sm:items-center w-full space-y-2 sm:space-y-0">
              <div className="h-4 bg-gray-700 rounded w-24 sm:w-20 md:w-20"></div>
              <div className="h-4 bg-gray-700 rounded w-32 sm:w-24 md:w-24"></div>
            </div>
          ))}
        </div>
        <div className="border-t border-[#333333] mt-5 sm:mt-4 md:mt-4 pt-4 sm:pt-3 md:pt-3 w-full">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center w-full space-y-2 sm:space-y-0">
            <div className="h-5 bg-gray-700 rounded w-28 sm:w-24 md:w-24"></div>
            <div className="h-5 bg-gray-700 rounded w-24 sm:w-20 md:w-20"></div>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!hasConfiguration) {
    return (
      <div className="bg-[#121212] p-5 sm:p-4 md:p-4 rounded-md mb-4 sm:mb-2 md:mb-2 w-full max-w-full border border-[#333333] shadow-md mt-0 sm:mt-12 md:mt-[6.3rem]">
        <h3 className="font-semibold text-white text-xl sm:text-lg md:text-lg mb-4 sm:mb-3 md:mb-3">Summary</h3>
        <div className="border-t border-[#333333] pt-4 sm:pt-3 md:pt-3 min-h-[200px]">
          {/* Empty content */}
        </div>
      </div>
    );
  }

  // Normal state with data
  const configurationEntries = renderConfigurationEntries();
  
  return (
    <div className="bg-[#121212] p-5 sm:p-4 md:p-4 rounded-md mb-4 sm:mb-2 md:mb-2 w-full max-w-full border border-[#333333] shadow-md mt-0 sm:mt-12 md:mt-[6.3rem]">
      <h3 className="font-semibold text-white text-xl sm:text-lg md:text-lg mb-4 sm:mb-3 md:mb-3">Summary</h3>
      
      <div className="border-t border-[#333333] pt-4 sm:pt-3 md:pt-3 space-y-4 sm:space-y-3 md:space-y-3 w-full">
        {configurationEntries && configurationEntries.length > 0 ? (
          configurationEntries
        ) : (
          <div className="text-gray-400 text-sm text-center py-6 sm:py-4 md:py-4">
            Configuration data not available
          </div>
        )}
      </div>
      
      <div className="border-t border-[#333333] mt-5 sm:mt-4 md:mt-4 pt-4 sm:pt-3 md:pt-3 w-full">
        {renderPriceSection()}
      </div>
    </div>
  );
};

// PropTypes for type checking
SummaryCard.propTypes = {
  configuration: PropTypes.object,
  price: PropTypes.string,
  isLoading: PropTypes.bool
};

export default React.memo(SummaryCard);